import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo, reviews, faqs } from '@/mocks/data';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = (form.querySelector('[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMsg('感谢您的留言！我们会尽快与您取得联系。');
      form.reset();
      return;
    }
    setFormStatus('loading');
    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9pk9odi2vhsdh2d366g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && (parsed as { code?: string })?.code === 'OK') {
        setFormStatus('success');
        setFormMsg('感谢您的留言！我们会尽快与您取得联系。');
        form.reset();
      } else {
        const msg = (parsed as { meta?: { message?: string } })?.meta?.message || '提交失败，请稍后再试';
        setFormStatus('error');
        setFormMsg(msg);
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请稍后再试');
    }
  };

  return (
    <main>
      <Breadcrumb items={[{ label: '关于我们' }]} />

      {/* Brand Introduction */}
      <section className="w-full py-8 md:py-14 bg-background-50">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading">关于91抖音</h1>
            <p className="text-sm text-foreground-500 mt-3">{aboutInfo.slogan}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">{aboutInfo.introduction}</p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">{aboutInfo.mission}</p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{aboutInfo.vision}</p>
              <div className="grid grid-cols-3 gap-4 mt-8">
                <div className="text-center p-4 rounded-xl bg-primary-100/70">
                  <p className="text-xl md:text-2xl font-bold text-primary-600 font-heading">{aboutInfo.users}</p>
                  <p className="text-xs text-foreground-500 mt-1">累计用户</p>
                </div>
                <div className="text-center p-4 rounded-xl bg-accent-100/70">
                  <p className="text-xl md:text-2xl font-bold text-accent-700 font-heading">{aboutInfo.dailyActive}</p>
                  <p className="text-xs text-foreground-500 mt-1">日均活跃</p>
                </div>
                <div className="text-center p-4 rounded-xl bg-secondary-100/70">
                  <p className="text-xl md:text-2xl font-bold text-secondary-700 font-heading">{aboutInfo.founded}</p>
                  <p className="text-xs text-foreground-500 mt-1">成立时间</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20creative%20tech%20office%20space%20with%20warm%20lighting%2C%20people%20collaborating%20on%20digital%20content%2C%20vibrant%20orange%20and%20golden%20accent%20colors%2C%20minimalist%20interior%2C%20energetic%20creative%20atmosphere%2C%20clean%20aesthetic%2C%20wide%20shot%20with%20natural%20light&width=800&height=600&seq=about-brand-illustration&orientation=landscape"
                alt="91抖音团队"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="w-full py-10 md:py-16 bg-background-100">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8 font-heading">核心价值观</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {aboutInfo.values.map((v, idx) => (
              <div key={idx} className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className={`text-xl ${['ri-user-heart-line', 'ri-award-line', 'ri-cpu-line', 'ri-shield-check-line'][idx]}`}></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2 font-heading">{v.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="w-full py-10 md:py-16 bg-background-50">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8 font-heading">用户口碑</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {reviews.map((review, idx) => (
              <div key={idx} className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                <div className="flex items-center gap-3 mb-3">
                  <img src={review.avatar} alt={review.name} className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-fill text-foreground-300'}`}></i>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                <p className="text-xs text-foreground-400 mt-3">{review.date}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="w-full py-10 md:py-16 bg-background-100">
        <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8 font-heading">常见问题</h2>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-4 text-sm font-medium text-foreground-900 text-left whitespace-normal cursor-pointer"
                >
                  {faq.question}
                  <i className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform flex-shrink-0 ml-2 ${openFaq === idx ? 'rotate-180' : ''}`}></i>
                </button>
                {openFaq === idx && (
                  <div className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.answer}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="w-full py-10 md:py-16 bg-background-50">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8 font-heading">联系我们</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">电子邮件</p>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">客服电话</p>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">公司地址</p>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">微信公众号</p>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <form onSubmit={handleContactSubmit} data-readdy-form="" className="space-y-4 p-6 rounded-xl bg-background-100 border border-background-200/70">
                <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">给我们留言</h3>
                <input
                  type="text"
                  name="company_alt"
                  className="antispam-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-foreground-600 mb-1.5">姓名</label>
                    <input type="text" name="name" required className="w-full px-3 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors" placeholder="请输入姓名" />
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-600 mb-1.5">邮箱</label>
                    <input type="email" name="email" required className="w-full px-3 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors" placeholder="请输入邮箱" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-foreground-600 mb-1.5">主题</label>
                  <input type="text" name="subject" className="w-full px-3 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors" placeholder="请输入主题" />
                </div>
                <div>
                  <label className="block text-sm text-foreground-600 mb-1.5">留言内容 <span className="text-foreground-400 text-xs">(最多500字)</span></label>
                  <textarea name="message" required maxLength={500} rows={4} className="w-full px-3 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none" placeholder="请输入留言内容"></textarea>
                </div>
                <button type="submit" disabled={formStatus === 'loading'} className="w-full py-2.5 text-sm font-medium whitespace-nowrap cursor-pointer rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-60">
                  {formStatus === 'loading' ? '提交中...' : '发送留言'}
                </button>
                {formStatus !== 'idle' && (
                  <p className={`text-xs text-center ${formStatus === 'success' ? 'text-accent-700' : 'text-primary-600'}`}>{formMsg}</p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        'mainEntity': faqs.map(f => ({
          '@type': 'Question',
          'name': f.question,
          'acceptedAnswer': { '@type': 'Answer', 'text': f.answer },
        })),
      }) }} />
    </main>
  );
}